import { useState } from 'react'
import type CrmCoreUser from '@/types/serializers/CrmCoreUser'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { columns } from './_components/columns'
import { DataTable } from './_components/DataTable'
import { UserDetailSheet } from './_components/UserDetailSheet'
import { InviteUserSheet } from './_components/InviteUserSheet'
import { TransferUserDialog } from './_components/TransferUserDialog'
import { RemoveUserDialog } from './_components/RemoveUserDialog'

interface UsersIndexProps {
  users: CrmCoreUser[]
  teams: { id: number; name: string }[]
}

export default function UsersIndex({ users, teams }: UsersIndexProps) {
  const [detailUserId, setDetailUserId] = useState<number | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [transferUser, setTransferUser] = useState<CrmCoreUser | null>(null)
  const [removeUser, setRemoveUser] = useState<CrmCoreUser | null>(null)

  // Resolve the open sheet's user from the live `users` prop on every render.
  // Membership mutations redirect back here with fresh props, but Inertia
  // preserves this component's local state, so holding the user as a state
  // snapshot would render stale memberships. Deriving by id keeps the sheet
  // in sync and drops it if the user disappears (e.g. removed).
  const detailUser =
    detailUserId !== null ? (users.find((u) => u.id === detailUserId) ?? null) : null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">
            Manage user accounts and invitations.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={users}
        onRowClick={(user) => setDetailUserId(user.id)}
        filterColumn="status"
        filterPlaceholder="Filter by status..."
      />

      <UserDetailSheet
        user={detailUser}
        teams={teams}
        onOpenChange={(open) => { if (!open) setDetailUserId(null) }}
        onTransfer={(user) => setTransferUser(user)}
        onRemove={(user) => setRemoveUser(user)}
      />

      <InviteUserSheet
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        teams={teams}
      />

      <TransferUserDialog
        user={transferUser}
        open={transferUser !== null}
        onOpenChange={(open) => { if (!open) setTransferUser(null) }}
        teams={teams}
      />

      <RemoveUserDialog
        user={removeUser}
        open={removeUser !== null}
        onOpenChange={(open) => { if (!open) setRemoveUser(null) }}
      />
    </div>
  )
}
